import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { runEditorCommand, type CommandId } from '../editor/commands';
import { useEditorStore } from '../store/editorStore';

export type MenuDialog = 'new' | 'projects' | 'cutting' | 'materials' | 'materialEditor' | 'generator' | 'group' | 'help';
type Action = { label: string; shortcut?: string; disabled?: boolean; run: () => void };

export function MenuBar({ openDialog, openCommands, save, exportProject, exportDxf }: {
  openDialog: (dialog: MenuDialog) => void;
  openCommands: () => void;
  save: () => void;
  exportProject: () => void;
  exportDxf: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const selectedId = useEditorStore((state) => state.selectedId);
  const selectedDrawingId = useEditorStore((state) => state.selectedDrawingId);
  const historyLength = useEditorStore((state) => state.history.length);
  const futureLength = useEditorStore((state) => state.future.length);
  const mode = useEditorStore((state) => state.mode);
  const command = (id: CommandId) => () => runEditorCommand(id);
  const dialog = (id: MenuDialog) => () => openDialog(id);
  useEffect(() => {
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(null); };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(null); };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, []);
  const menus: Array<{ id: string; items: Action[] }> = [
    { id: 'file', items: [
      { label: t('project.new'), shortcut: 'Ctrl+N', run: dialog('new') },
      { label: t('project.open'), shortcut: 'Ctrl+O', run: dialog('projects') },
      { label: t('project.save'), shortcut: 'Ctrl+S', run: save },
      { label: t('transfer.import'), run: dialog('projects') },
      { label: t('transfer.backup'), run: exportProject },
      { label: t('transfer.dxf'), run: exportDxf },
      { label: t('cut.title'), run: dialog('cutting') },
    ] },
    { id: 'edit', items: [
      { label: t('toolbar.undo'), shortcut: 'Ctrl+Z', disabled: historyLength === 0, run: () => useEditorStore.getState().undo() },
      { label: t('toolbar.redo'), shortcut: 'Ctrl+Y', disabled: futureLength === 0, run: () => useEditorStore.getState().redo() },
      { label: t('tool.select'), shortcut: 'V', run: command('select') },
      { label: t('tool.copy'), shortcut: 'Ctrl+D', disabled: !selectedId && !selectedDrawingId, run: command('copy') },
      { label: t('tool.move'), shortcut: 'G', disabled: !selectedId, run: command('move') },
      { label: t('tool.rotate'), shortcut: 'R', disabled: !selectedId, run: command('rotate') },
      { label: t('tool.scale'), shortcut: 'S', disabled: !selectedId, run: command('scale') },
      { label: t('tool.mirror'), disabled: !selectedId && !selectedDrawingId, run: command('mirror') },
      { label: t('tool.pushPull'), run: command('pushPull') },
      { label: t('tool.group'), run: dialog('group') },
      { label: t('tool.deleteObject'), shortcut: 'Delete', disabled: !selectedId && !selectedDrawingId, run: command('delete') },
    ] },
    { id: 'view', items: [
      { label: t('mode.2d'), disabled: mode === '2d', run: command('view2d') },
      { label: t('mode.3d'), disabled: mode === '3d', run: command('view3d') },
      { label: t('camera.perspective'), run: command('perspective') },
      { label: t('camera.top'), run: command('top') },
      { label: t('camera.front'), run: command('front') },
      { label: t('camera.right'), run: command('right') },
      { label: t('toolbar.focus'), shortcut: 'F', run: command('focus') },
      { label: t('status.grid'), run: command('toggleGrid') },
      { label: t('status.snap'), run: command('toggleSnap') },
      { label: 'ORTHO', run: command('toggleOrtho') },
      { label: t('tool.fullscreen'), shortcut: 'Tab', run: command('fullscreen') },
      { label: t('mode.presentation'), run: () => useEditorStore.getState().setMode('presentation') },
    ] },
    { id: 'insert', items: [
      { label: t('library.baseCabinet'), run: command('cabinet') },
      { label: t('generator.title'), run: dialog('generator') },
      { label: t('tool.shelf'), disabled: !selectedId, run: command('shelf') },
      { label: t('tool.door'), disabled: !selectedId, run: command('door') },
      { label: t('tool.drawer'), disabled: !selectedId, run: command('drawer') },
    ] },
    { id: 'tools', items: [
      { label: t('cut.title'), run: dialog('cutting') },
      { label: t('tool.materials'), run: dialog('materials') },
      { label: t('tool.materialEditor'), run: dialog('materialEditor') },
      { label: t('app.commands'), shortcut: 'Ctrl+K', run: openCommands },
    ] },
    { id: 'help', items: [
      { label: t('help.shortcuts'), run: dialog('help') },
      { label: t('help.about'), run: dialog('help') },
    ] },
  ];
  return <div className="menu-bar" ref={root} role="menubar">
    {menus.map((menu) => <div className="menu-group" key={menu.id}>
      <button type="button" role="menuitem" aria-haspopup="menu" aria-expanded={open === menu.id} className={open === menu.id ? 'open' : ''} onClick={() => setOpen(open === menu.id ? null : menu.id)} onMouseEnter={() => { if (open && open !== menu.id) setOpen(menu.id); }}>{t(`menu.${menu.id}`)}</button>
      {open === menu.id && <div className="menu-popup" role="menu">
        {menu.items.map((item) => <button type="button" role="menuitem" key={item.label} disabled={item.disabled} onClick={() => { setOpen(null); item.run(); }}><span>{item.label}</span>{item.shortcut && <kbd>{item.shortcut}</kbd>}</button>)}
      </div>}
    </div>)}
  </div>;
}
